import type { CourtBoardEntry } from "../types/generated";

export type CourtOperationalState = CourtBoardEntry["operational_state"];
export type CourtOccupancyState = CourtBoardEntry["occupancy_state"];

export interface CourtStateInput {
  operationalState: CourtOperationalState;
  occupancyState: CourtOccupancyState;
}

export interface CourtStatusAnnouncementInput extends CourtStateInput {
  name: string;
  playerCount: number;
  capacity: number;
  availableSlots: number;
  isPrivate: boolean;
  lineGroupCount: number;
  linePlayerCount: number;
}

const OPERATIONAL_STATE_LABELS: Record<CourtOperationalState, string> = {
  operational: "Operational",
  maintenance: "Maintenance",
  closed: "Closed",
};

const OCCUPANCY_STATE_LABELS: Record<CourtOccupancyState, string> = {
  available: "Available",
  waiting: "Waiting",
  in_play: "In play",
  reserved: "Reserved",
};

export function getCourtStateLabel({
  operationalState,
  occupancyState,
}: CourtStateInput): string {
  return operationalState === "operational"
    ? OCCUPANCY_STATE_LABELS[occupancyState]
    : OPERATIONAL_STATE_LABELS[operationalState];
}

export function formatCourtLineSummary(
  groupCount: number,
  playerCount: number,
): string | null {
  if (groupCount <= 0) return null;
  return `${groupCount} ${groupCount === 1 ? "party" : "parties"} / ${playerCount} ${
    playerCount === 1 ? "player" : "players"
  } waiting`;
}

export function formatCourtStatusAnnouncement({
  name,
  operationalState,
  occupancyState,
  playerCount,
  capacity,
  availableSlots,
  isPrivate,
  lineGroupCount,
  linePlayerCount,
}: CourtStatusAnnouncementInput): string {
  const parts = [
    name,
    OPERATIONAL_STATE_LABELS[operationalState],
    OCCUPANCY_STATE_LABELS[occupancyState],
    `${playerCount} of ${capacity} players`,
  ];
  if (occupancyState === "in_play") {
    parts.push(
      isPrivate
        ? "Private game"
        : `${availableSlots} ${
            availableSlots === 1 ? "slot" : "slots"
          } available`,
    );
  }
  const lineSummary = formatCourtLineSummary(lineGroupCount, linePlayerCount);
  if (lineSummary) parts.push(lineSummary);

  return parts.join(", ");
}
